'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import NextLink, { type LinkProps as NextLinkProps } from 'next/link'
import { usePathname } from 'next/navigation'
import { Icon } from '@/icons/Icon'
import { isEfficientReferralUrl } from '@/lib/rich-text-link-url'
import { cn } from '@/lib/utils'
import { trackAffiliateLinkClick } from '@/lib/analytics'

type LinkProps = VariantProps<typeof variants> &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> &
  NextLinkProps & {
    external?: boolean
    variant?: 'block' | 'inline' | 'hero'
    theme?: 'white' | 'primary'
    state?: 'active' | 'disabled' | 'default'
    custom?: string
    passReferrerUrl?: boolean // Whether to send referrer data for outbound links.
    followExternal?: boolean // Skip nofollow for trusted external links (e.g. own social profiles)
    sponsored?: boolean // Add rel="sponsored" — only for partner/affiliate efficient.link/r links
  }

const variants = cva('inline-flex items-center gap-1', {
  variants: {
    variant: {
      block: 'w-fit text-13 font-semibold text-primary-500 underline-offset-2 hover:underline',
      inline: 'inline text-primary-500 underline-offset-4 transition-colors hover:underline',
    },
    theme: {
      white: 'text-white [&>svg>path]:stroke-white',
      primary: 'text-primary-500 [&>svg>path]:stroke-primary-500',
    },
    state: {
      default: '',
      active: '',
      disabled: '',
    },
    transition: {
      true: 'transition-colors',
    },
  },
})

export default function Link({
  children,
  href,
  target,
  followExternal,
  external,
  variant,
  theme,
  state,
  custom,
  className,
  passReferrerUrl,
  referrerPolicy: _callerReferrerPolicy,
  sponsored,
  ...props
}: LinkProps) {
  const pathname = usePathname()
  const isActive = pathname === href ? 'active' : 'default'
  if (!state) {
    state = isActive
  }

  const hrefString = typeof href === 'string' ? href : ''
  const normalizedHref = hrefString.toLowerCase()

  const isAbsoluteUrl =
    normalizedHref.startsWith('http://') || normalizedHref.startsWith('https://')
  const isEmailUrl = normalizedHref.startsWith('mailto:')
  const isFirstParty =
    isAbsoluteUrl && /^https?:\/\/([^/]+\.)?efficient\.app(\/|$)/.test(normalizedHref)
  const isReferralUrl = isEfficientReferralUrl(hrefString)
  const referrerPolicy = passReferrerUrl ? 'no-referrer-when-downgrade' : 'origin'

  if (!href) return null

  const sharedProps = {
    className: cn([variants({ variant, theme, state }), className]),
    style: { color: custom } as React.CSSProperties,
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isReferralUrl) {
      trackAffiliateLinkClick(hrefString)
    }
    props.onClick?.(e)
  }

  // Absolute and email URLs use a plain <a> tag — NextLink is for internal navigation only
  if (isAbsoluteUrl || isEmailUrl) {
    // Filter out NextLink-specific props that aren't valid on <a> tags
    const { prefetch, scroll, replace, onNavigate, onClick: _, ...htmlProps } = props
    // rel and referrerPolicy are placed AFTER the spread so callers can't accidentally override them
    // First-party links and followExternal (e.g. own social profiles) skip nofollow.
    // `sponsored` is only added for referral links that callers explicitly mark as partner/
    // affiliate links — a tracked efficient.link/r link for a non-partner app is just nofollow.
    const rel = isEmailUrl
      ? target === '_blank'
        ? 'noopener'
        : undefined
      : isFirstParty || followExternal
        ? 'noopener'
        : isReferralUrl && sponsored
          ? 'noopener nofollow sponsored'
          : 'noopener nofollow'
    return (
      <a
        href={hrefString}
        target={target || (isEmailUrl ? undefined : '_blank')}
        {...sharedProps}
        {...htmlProps}
        onClick={handleClick}
        rel={rel}
        {...(!isEmailUrl && !isFirstParty && { referrerPolicy })}
      >
        {children}
        {external && <Icon name="share-right" className="aspect-square w-4" />}
      </a>
    )
  }

  return (
    <NextLink href={href} target={target} {...sharedProps} {...props}>
      {children}
      {external && <Icon name="share-right" className="aspect-square w-4" />}
    </NextLink>
  )
}
